<template>
  <v-card>
    <v-row class="ma-0">
      <v-col cols="12" sm="7" class="chart-source-overview-col pa-0">
        <v-card-title v-t="'candidatesSources'" class="align-start font-weight-semibold" />

        <v-card-text>
          <vue-apex-charts
            id="chart-sales-overview"
            ref="recruitmentSourceOverview"
            type="donut"
            :options="chartOptions"
            :series="chartData"
          ></vue-apex-charts>
        </v-card-text>
      </v-col>
      <v-col cols="12" sm="5" class="pa-0">
        <v-card-title>
          <span v-t="'topSources'" class="me-3" />
        </v-card-title>

        <v-card-text>
          <v-list>
            <v-list-item
              v-for="(source, index) in mostUsedSources.labels"
              :key="index"
              :class="`d-flex align-center px-0 ${index > 0 ? 'mt-3' : ''}`"
            >
              <div class="d-flex align-center flex-grow-1 flex-wrap">
                <div class="me-2">
                  <v-list-item-title class="text-sm font-weight-semibold text-pre-wrap">
                    <div :class="`stats-dot ${chartColorsNames[index]} d-inline-block rounded-circle me-2`"></div>
                    {{ source }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-xs text-pre-wrap">
                    {{ mostUsedSources.categories[index] }}
                  </v-list-item-subtitle>
                </div>

                <v-spacer></v-spacer>

                <div class="d-flex align-center">
                  <h4 class="text-base font-weight-semibold me-1">
                    {{ mostUsedSources.data[index] }}
                  </h4>
                </div>
              </div>
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import { mdiDotsVertical, mdiCurrencyUsd } from '@mdi/js'
import { useState } from 'vuex-composition-helpers'
import { getVuetify, addAlpha } from '@core/utils'
import { watch, ref } from '@vue/composition-api'

export default {
  components: {
    VueApexCharts: () => import('vue-apexcharts'),
  },
  setup(_, vm) {
    const { recruitment } = useState('dashboard', ['recruitment'])

    const $vuetify = getVuetify()

    const chartColorsNames = ['primary', 'success', 'accent', 'info', 'secondary']

    const mostUsedSources = {
      labels: recruitment.value.countedSources.labels.slice(0, 5),
      data: recruitment.value.countedSources.data.slice(0, 5),
      categories: recruitment.value.countedSources.categories.slice(0, 5),
    }

    const recruitmentSourceOverview = ref(null)

    watch(
      () => [vm.root.$i18n.locale],
      () => {
        recruitmentSourceOverview.value.updateOptions({
          plotOptions: {
            pie: {
              donut: {
                labels: {
                  total: {
                    label: vm.root.$t('candidatesFromSources'),
                  },
                },
              },
            },
          },
        })
      },
    )

    const chartOptions = {
      legend: {
        show: false,
      },
      colors: [
        $vuetify.theme.currentTheme.primary,
        addAlpha($vuetify.theme.currentTheme.success, 0.7),
        addAlpha($vuetify.theme.currentTheme.accent, 0.7),
        addAlpha($vuetify.theme.currentTheme.info, 0.8),
        addAlpha($vuetify.theme.currentTheme.secondary, 0.5),
      ],
      dataLabels: {
        enabled: false,
      },
      plotOptions: {
        pie: {
          donut: {
            size: '80%',
            labels: {
              show: true,
              name: {
                fontSize: '12px',
                fontFamily: 'Inter", sans-serif',
                offsetY: 25,
              },
              value: {
                fontSize: '2.125rem',
                fontFamily: 'Inter", sans-serif',
                fontWeight: 600,
                offsetY: -15,
              },
              total: {
                show: true,
                label: vm.root.$t('candidatesFromSources'),
                color: 'rgba(94, 86, 105, 0.68)',
                formatter(value) {
                  return `${value.globals.seriesTotals.reduce((total, num) => total + num)}`
                },
              },
            },
          },
        },
      },
      labels: recruitment.value.countedSources.labels,
    }

    const chartData = recruitment.value.countedSources.data

    return {
      countedSources: recruitment.value.countedSources,
      chartColorsNames,
      mostUsedSources,
      chartOptions,
      chartData,
      recruitmentSourceOverview,
      icons: {
        mdiDotsVertical,
        mdiCurrencyUsd,
      },
    }
  },
}
</script>

<style lang="scss">
@import '~@core/preset/preset/mixins.scss';
@import '~vuetify/src/styles/styles.sass';
@media #{map-get($display-breakpoints, 'sm-and-up')} {
  .chart-source-overview-col {
    @include ltr() {
      border-right: thin solid;
    }
    @include rtl() {
      border-left: thin solid;
    }
  }
}
@include theme--child(chart-source-overview-col) using ($material) {
  @media #{map-get($display-breakpoints, 'sm-and-up')} {
    border-color: map-get($material, 'dividers');
  }
}
#chart-sales-overview {
  .apexcharts-canvas {
    .apexcharts-text {
      &.apexcharts-datalabel-value {
        font-weight: 600;
      }
      &.apexcharts-datalabel-label {
        font-size: 1rem;
      }
    }
  }
}
</style>
